/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, isCurrentOwnerPage, resolveOwnerSlug } from "@cosmos/hygraph-data";
import { Locale, CosmosPageBlogDetailMetadataQuery } from "@cosmos/hygraph-schema";
import { getSlugParam, getHygraphLocale } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";
import { notFound } from "next/navigation";

import { CosmosPageMetadata } from "../common/page.types";
import { checkValidParams } from "../templates/selector";

const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
): Promise<CosmosPageBlogDetailMetadataQuery> => {
	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	if (!slug) {
		throw new Error("Missing slug");
	}

	// Transform URL path to Hygraph slug by applying owner prefix
	const hygraphSlug = await resolveOwnerSlug(slug, preview);

	const resp = await getHygraphCosmosPageData(
		"CosmosPageBlogDetailMetadata",
		{
			locale: [locale, Locale.En] as Locale[],
			slug: hygraphSlug,
		},
		preview,
	);

	// Tenant ownership guard: don't surface a foreign-owned page's metadata on this site.
	const page = resp?.cosmosPageBlogDetail;
	if (page && !(await isCurrentOwnerPage(page, preview))) {
		return {};
	}

	return resp;
};

export async function cosmosPageBlogDetailMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
}: CosmosPageMetadata): Promise<Metadata> {
	const valid = checkValidParams({ params, searchParams });
	if (!valid) {
		return notFound();
	}

	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: CosmosPageBlogDetailMetadataQuery = {};
	try {
		pageData = await getPageMetadata(getHygraphLocale(hygraphLocales, locale), slug, searchParams.preview);
	} catch (e) {
		console.error("Failed to get cosmosPageBlogDetailMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData.cosmosPageBlogDetail?.metaTags || {};

	return getAppMetadata(metaTags, slug, locale);
}
